import React, { useState } from 'react'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { pick } from 'ramda'
import { routes } from 'core/utils/routes'
import DocumentMeta from 'core/components/DocumentMeta'
import DataKeys from 'k8s/DataKeys'
import { ArrayElement } from 'core/actions/Action'
import ListContainer from 'core/containers/ListContainer'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useGlobalParams from 'core/hooks/useGlobalParams'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import InferActionParams from 'core/actions/InferActionParams'
import { listPodDisruptionBudgets } from './actions'
import { podDisruptionBudgetsSelector } from './selectors'
import { useSelector } from 'react-redux'
import usePluginRouter from 'core/hooks/usePluginRouter'
import { IPodDisruptionBudgetDetailsPageTabs } from './model'
import ClusterNameCell from 'k8s/components/common/cells/ClusterNameCell'
import { durationBetweenDates } from 'utils/misc'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import AddPodDisruptionBudgetModal from './AddPodDisruptionBudgetModal'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import DeletePodDisruptionBudgetDialog from './DeletePodDisruptionBudgetDialog'

type ModelDataKey = DataKeys.PodDisruptionBudgets
type SelectorModel = ArrayElement<ReturnType<typeof podDisruptionBudgetsSelector>>
type ActionParams = InferActionParams<typeof listPodDisruptionBudgets>
//@fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {
  masterNodeClusters: boolean
  healthyClusters: boolean
}

const requiredParams: Array<keyof ActionParams> = ['clusterId', 'namespace']
const defaultParams: Params & SortingState = {
  clusterId: null,
  namespace: null,
  masterNodeClusters: true,
  healthyClusters: true,
  orderBy: 'name',
  orderDirection: 'asc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'PodDisruptionBudgets',
  listTablePrefs,
)

const searchTargets = ['name', 'clusterName']

export const getPodDisruptionBudgetsColumns = (
  pluginId = 'kubernetes',
): GridViewColumn<SelectorModel>[] => {
  const routePath = pluginId === 'kubevirt' ? 'kubevirtConfiguration' : 'configuration'
  return [
    {
      key: 'name',
      label: 'Name',
      width: 'medium',
      CellComponent: createGridLinkCell({
        routeToFn: ({ clusterId, id }) =>
          routes[routePath].podDisruptionBudgets.details.path({
            clusterId,
            id,
            tab: IPodDisruptionBudgetDetailsPageTabs.Overview,
          }),
      }),
    },
    { key: 'clusterName', label: 'Cluster', width: 'medium', CellComponent: ClusterNameCell },
    { key: 'namespace', label: 'Namespace' },
    {
      key: 'created',
      label: 'Age',
      formatFn: (value) => durationBetweenDates({ labels: ['d'] })(value),
    },
    { key: 'spec.minAvailable', label: 'Min Available' },
    { key: 'spec.maxUnavailable', label: 'Max Unavailable' },
    { key: 'status.desiredHealthy', label: 'Desired Healthy' },
    { key: 'status.currentHealthy', label: 'Current Healthy' },
    {
      key: 'labels',
      label: 'Labels',
      disableSorting: true,
      CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
    },
    {
      key: 'annotations',
      label: 'Annotations',
      disableSorting: true,
      CellComponent: createResourceLabelsCell({ type: 'table', separator: ': ' }),
    },
  ]
}

export default function PodDisruptionBudgetsListPage() {
  const { currentPluginId } = usePluginRouter()
  const routePath = currentPluginId === 'kubevirt' ? 'kubevirtConfiguration' : 'configuration'
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { message, loading, reload } = useListAction(listPodDisruptionBudgets, {
    params,
    requiredParams,
  })
  const podDisruptionBudgets = useSelector(podDisruptionBudgetsSelector)

  const columns = getPodDisruptionBudgetsColumns(currentPluginId)

  const [selectedPodDisruptionBudget, setSelectedPodDisruptionBudget] = useState<SelectorModel>(
    null,
  )
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)

  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = [
    {
      label: 'Delete',
      icon: 'trash-alt',
      handleClick: (podDisruptionBudget) => {
        setSelectedPodDisruptionBudget(podDisruptionBudget)
        setShowDeleteDialog(true)
      },
      refreshAfterSuccess: true,
    },
  ]

  return (
    <>
      <DocumentMeta title="Pod Disruption Budgets" />
      <AddPodDisruptionBudgetModal addRoute={routes[routePath].podDisruptionBudgets.add} />
      {showDeleteDialog && (
        <DeletePodDisruptionBudgetDialog
          onClose={() => setShowDeleteDialog(false)}
          rows={[selectedPodDisruptionBudget]}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.PodDisruptionBudgets}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={podDisruptionBudgets}
        columns={columns}
        addUrl={routes[routePath].podDisruptionBudgets.add.path()}
        addText="Add Pod Disruption Budgets"
        getParamsUpdater={getParamsUpdater}
        rowMenuItems={rowMenuItems}
        showItemsCountInLabel
        showRowMenuForSingleRowActions
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
